// src/pages/TermsPage.jsx
import React from "react";
import { SCHOOL } from "../config";

export default function TermsPage() {
  return (
    <div style={{ maxWidth: "760px", margin: "0 auto", padding: "40px 20px 80px", lineHeight: 1.7, color: "#1F2937" }}>
      <h1 style={{ color: "#7B1515", fontSize: "1.6rem", marginBottom: "4px" }}>Terms &amp; Conditions</h1>
      <p style={{ color: "#6B7280", fontSize: ".85rem", marginBottom: "28px" }}>
        Last updated: October 2026 · Applies to the {SCHOOL.name} Student Portal (web and Android)
      </p>

      <p>
        These terms govern your use of the {SCHOOL.name} Student Portal (the "Portal") — our website and Android
        app. By registering for an account or using the Portal, you agree to them. If you do not agree, please do
        not use the Portal.
      </p>

      <h2 style={sectionStyle}>About us</h2>
      <p>
        {SCHOOL.name} is a music school operated by {SCHOOL.teacher}, teaching Indian classical vocal and
        harmonium. We are based at {SCHOOL.address}. You can reach us at {SCHOOL.email} or {SCHOOL.phone}.
      </p>

      <h2 style={sectionStyle}>What we provide</h2>
      <p>
        <strong>Our classes are taught in person at our premises in Pune.</strong> The Portal is a companion tool
        for enrolled students — it is not an online course, and we do not sell digital content or online tuition
        through it.
      </p>
      <p>Through the Portal, enrolled students can:</p>
      <ul style={listStyle}>
        <li>Register and manage their enrollment details</li>
        <li>Access practice material for their assigned course — audio recordings, backing tracks, and notation</li>
        <li>View fees due, pay their class fees, and download receipts</li>
        <li>Receive announcements and reminders from the school</li>
      </ul>
      <p>
        All Portal features are available to enrolled students at no additional charge. Access to course material
        is <strong>not</strong> conditional on fee payment — it is governed by your enrollment status, which is
        managed by the school.
      </p>

      <h2 style={sectionStyle}>Fees and what you are paying for</h2>
      <p>
        Any payment made through the Portal is for <strong>in-person class tuition</strong> or for school events
        and activities. You are not purchasing app features, digital content, or an online service.
      </p>
      <ul style={listStyle}>
        <li><strong>Monthly tuition:</strong> charged per student and varying by batch, according to the type of
          batch, the examination level, and the level of expertise taught. Your applicable amount is shown in the
          Portal.</li>
        <li><strong>Normally no upfront fee on joining.</strong> In most cases a new student attends for a month
          first, with that month's fee payable once it is complete. From the following month onwards, fees are
          payable in advance by the 7th of the month. We do not collect fees more than one month ahead.</li>
        <li><strong>Event and activity fees:</strong> occasional charges for concerts, examinations, and school
          programmes. Amounts vary by event and are communicated in advance.</li>
        <li>All amounts are in Indian Rupees (INR).</li>
        <li>Where an adjusted amount applies to a student, that adjusted amount is what the Portal shows and what
          is payable.</li>
      </ul>
      <p>
        Fees may be revised from time to time. We will give reasonable notice before any change takes effect.
      </p>

      <h2 style={sectionStyle}>Payments</h2>
      <p>
        Online payments are processed by <strong>Cashfree Payments</strong>, our payment gateway. We do not receive
        or store your card, UPI, or bank account details — these are handled entirely by Cashfree under their own
        terms and security standards.
      </p>
      <p>
        You may also pay by cash or bank transfer directly to the school and report it through the Portal; such
        payments appear as pending until confirmed by the school.
      </p>

      <h2 style={sectionStyle}>Your account</h2>
      <ul style={listStyle}>
        <li>Many of our students are minors. Where a student is under 18, a parent or guardian must register and
          hold the account on their behalf.</li>
        <li>You are responsible for keeping your login credentials secure and for activity under your account.</li>
        <li>Please keep your contact details accurate so we can reach you about classes and fees.</li>
        <li>Registration is subject to approval by the school. Submitting a registration does not by itself
          guarantee admission.</li>
      </ul>

      <h2 style={sectionStyle}>Course material and acceptable use</h2>
      <p>
        Recordings, backing tracks, notation and other material in the Portal are created or curated by the school
        and provided for your personal practice only.
      </p>
      <ul style={listStyle}>
        <li>Do not share, redistribute, upload, or publish our material — including to social media, messaging
          groups, or other platforms.</li>
        <li>Do not use the material commercially or for teaching others.</li>
        <li>Do not share your account with anyone outside your immediate family.</li>
        <li>Do not attempt to disrupt, reverse-engineer, or gain unauthorised access to the Portal.</li>
      </ul>

      <h2 style={sectionStyle}>Attendance and classes</h2>
      <p>Class schedules are assigned by the school.</p>
      <ul style={listStyle}>
        <li><strong>Public holidays</strong> are holidays for the school. These classes are not rescheduled.</li>
        <li><strong>If we cancel a class</strong> for any other reason, we offer a replacement class at a
          mutually convenient time.</li>
        <li><strong>Classes missed by the student</strong> are not carried forward, as fees reserve a place in a
          batch rather than paying per class attended.</li>
        <li><strong>Extra classes for events, competitions and performances</strong> are provided at no
          additional charge. When students are preparing for a programme we take additional sessions as needed.</li>
      </ul>
      <p>
        See our <a href="/refunds" style={linkStyle}>Refunds &amp; Cancellations policy</a> for the full detail.
      </p>

      <h2 style={sectionStyle}>Ending your enrollment</h2>
      <p>
        You may discontinue classes at any time by informing the school. We may suspend or end a student's
        enrollment where fees remain unpaid over an extended period, where these terms are breached, or where
        conduct is disruptive to other students. Where we end an enrollment, we will tell you why.
      </p>

      <h2 style={sectionStyle}>Availability and liability</h2>
      <p>
        We try to keep the Portal available and accurate, but we do not guarantee uninterrupted access. The Portal
        is a convenience alongside your classes — it is not the service itself, and a temporary outage does not
        affect your tuition. We are not liable for indirect or consequential loss arising from use of the Portal.
        Nothing here limits any liability that cannot be limited under Indian law.
      </p>

      <h2 style={sectionStyle}>Changes to these terms</h2>
      <p>
        We may update these terms as the school or the Portal changes. The "last updated" date above will reflect
        any revision, and continued use after a change means you accept it.
      </p>

      <h2 style={sectionStyle}>Governing law</h2>
      <p>
        These terms are governed by the laws of India, and the courts at Pune, Maharashtra have jurisdiction over
        any dispute.
      </p>

      <h2 style={sectionStyle}>Contact us</h2>
      <p>
        {SCHOOL.name}<br />
        {SCHOOL.address}<br />
        Email: <a href={`mailto:${SCHOOL.email}`} style={linkStyle}>{SCHOOL.email}</a><br />
        Phone: <a href={`tel:${SCHOOL.phone.replace(/\s/g, "")}`} style={linkStyle}>{SCHOOL.phone}</a>
      </p>
      <p style={{ marginTop: "28px", fontSize: ".9rem" }}>
        See also our <a href="/privacy" style={linkStyle}>Privacy Policy</a> and{" "}
        <a href="/refunds" style={linkStyle}>Refunds &amp; Cancellations policy</a>.
      </p>
    </div>
  );
}

const sectionStyle = { color: "#7B1515", fontSize: "1.05rem", marginTop: "28px", marginBottom: "8px" };
const listStyle    = { paddingLeft: "20px", marginBottom: "12px" };
const linkStyle    = { color: "#7B1515", fontWeight: 600 };
